import { useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Printer, Package, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BUCKET_LABELS = {
  grocery: 'Groceries (1%)',
  personal_hygiene: 'Personal Hygiene (1%)',
  prepared_food: 'Prepared Food (6%)',
  merchandise: 'General Merchandise (6%)',
  exempt: 'Exempt (0%)',
};

export default function AdminTaxReports({ user, onLogout }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auditPreview, setAuditPreview] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDownloading, setAuditDownloading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/tax/st1-report`, { params: { year, month } });
      setReport(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Could not load report: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fmt) => {
    try {
      const res = await api.get(`/admin/tax/st1-report.${fmt}`, {
        params: { year, month },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dealshaq-st1-${year}-${String(month).padStart(2, '0')}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Download failed: ${detail}`);
    }
  };

  const previewAuditPackage = async () => {
    setAuditLoading(true);
    setAuditPreview(null);
    try {
      const res = await api.get(`/admin/tax/audit-package/preview`, { params: { year, month } });
      setAuditPreview(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Could not build audit package: ${detail}`);
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadAuditPackage = async () => {
    setAuditDownloading(true);
    try {
      const res = await api.get(`/admin/tax/audit-package.zip`, {
        params: { year, month },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DealShaq-Audit-Package-${year}-${String(month).padStart(2, '0')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit package downloaded');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Download failed: ${detail}`);
    } finally {
      setAuditDownloading(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="tax-reports-title">Tax Reports · ST-1 / ST-1A</h1>
          <p className="text-sm text-gray-600 mt-1">
            Virginia Marketplace Facilitator filing helper. First filing due Aug 20 for July activity.
            Cert #10-992929133F-001.
          </p>
        </div>

        {/* Phase banner — clarifies this is the internal reporting layer, not auto-submit. */}
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="tax-reports-phase-banner"
        >
          <p className="font-semibold">Phase 1 · Internal Reporting & Audit</p>
          <p className="text-xs mt-1">
            This page generates the data DealShaq will file with Virginia each month. Phase 3 (Auto-Submit Engine)
            will electronically submit ST-1 returns + trigger ACH payment via the Business iFile API once VA
            approves DealShaq as a registered software provider.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Select period</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="year">Year</Label>
              <Input id="year" type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value || '0'))}
                     className="w-28" data-testid="tax-year-input" />
            </div>
            <div>
              <Label htmlFor="month">Month</Label>
              <Input id="month" type="number" min="1" max="12" value={month}
                     onChange={(e) => setMonth(parseInt(e.target.value || '0'))}
                     className="w-24" data-testid="tax-month-input" />
            </div>
            <Button onClick={fetchReport} disabled={loading} data-testid="tax-run-button">
              {loading ? 'Loading…' : 'Run report'}
            </Button>
            {report && (
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => downloadFile('csv')} data-testid="tax-csv-button">
                  <Download className="w-4 h-4 mr-2" />CSV
                </Button>
                <Button variant="outline" onClick={() => downloadFile('pdf')} data-testid="tax-pdf-button">
                  <FileText className="w-4 h-4 mr-2" />PDF
                </Button>
                <Button variant="outline" onClick={() => window.print()}>
                  <Printer className="w-4 h-4 mr-2" />Print
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* One-Click Audit Package — bundles ledger + reconciliation + ST-1 into one ZIP. */}
        <Card className="border-teal-200" data-testid="audit-package-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
              One-Click Audit Package
            </CardTitle>
            <CardDescription>
              Bundle the immutable tax transaction log, monthly reconciliation logs, and the
              ST-1 / ST-1A filing files for {year}-{String(month).padStart(2, '0')} into a single
              checksummed ZIP — an auditor request becomes a one-second download.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={previewAuditPackage} disabled={auditLoading}
                      data-testid="audit-preview-button">
                {auditLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Preview contents
              </Button>
              <Button onClick={downloadAuditPackage} disabled={auditDownloading}
                      className="bg-teal-600 hover:bg-teal-700" data-testid="audit-download-button">
                {auditDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                Download audit package (.zip)
              </Button>
            </div>

            {auditPreview && (
              <div className="rounded-lg border bg-slate-50 p-4 space-y-3" data-testid="audit-preview-panel">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    ['Ledger rows', auditPreview.counts.tax_transaction_log_rows],
                    ['Distinct orders', auditPreview.counts.distinct_orders],
                    ['Reconciliation docs', auditPreview.counts.reconciliation_monthly_docs],
                    ['Tax collected', `$${Number(auditPreview.report_totals?.tax_collected || 0).toFixed(2)}`],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                      <p className="text-lg font-bold mt-0.5"
                         data-testid={`audit-count-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Badge variant="secondary">{auditPreview.mode}</Badge>
                  <Badge variant={auditPreview.counts.st1_filing_staged ? 'default' : 'outline'}>
                    ST-1 {auditPreview.counts.st1_filing_staged ? 'staged' : 'not staged'}
                  </Badge>
                  <span>{auditPreview.files.length} files · integrity via SHA-256</span>
                </div>
                <div className="border-t pt-2">
                  <p className="text-xs text-gray-500 mb-1">Included files:</p>
                  <ul className="text-xs font-mono text-gray-700 space-y-0.5 max-h-40 overflow-y-auto">
                    {auditPreview.files.map((f) => (
                      <li key={f.path} className="flex justify-between gap-4">
                        <span className="truncate">{f.path}</span>
                        <span className="text-gray-400 whitespace-nowrap">{f.bytes} B</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {report && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Totals · {report.period.label}</CardTitle>
                <CardDescription>{report.filer.name} · {report.filer.jurisdiction}</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ['Gross Sales', `$${report.totals.gross_sales.toFixed(2)}`],
                  ['Taxable Sales', `$${report.totals.taxable_sales.toFixed(2)}`],
                  ['Tax Collected', `$${report.totals.tax_collected.toFixed(2)}`],
                  ['Orders', report.totals.order_count],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border bg-slate-50 p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold mt-1" data-testid={`tax-total-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>By Tax Bucket</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-gray-500 uppercase border-b">
                    <tr><th className="py-2">Bucket</th><th className="text-right">Taxable Sales</th><th className="text-right">Tax Collected</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.by_bucket).map(([bucket, vals]) => (
                      <tr key={bucket} className="border-b">
                        <td className="py-2">{BUCKET_LABELS[bucket] || bucket}</td>
                        <td className="text-right">${vals.approx_taxable_sales.toFixed(2)}</td>
                        <td className="text-right font-semibold">${vals.tax.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Per-Location (ST-1A)</CardTitle>
                <CardDescription>Use this breakdown if filing ST-1A for multi-location retailers.</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-gray-500 uppercase border-b">
                    <tr><th className="py-2">Retailer</th><th className="text-right">Orders</th><th className="text-right">Gross Sales</th><th className="text-right">Tax Collected</th></tr>
                  </thead>
                  <tbody>
                    {report.by_retailer.length === 0 ? (
                      <tr><td colSpan="4" className="py-4 text-center text-gray-500">No orders in this period</td></tr>
                    ) : report.by_retailer.map((r) => (
                      <tr key={r.drlp_id} className="border-b">
                        <td className="py-2">
                          <p className="font-medium">{r.drlp_name}</p>
                          <p className="text-xs text-gray-500 font-mono">{r.drlp_id.slice(0, 8)}</p>
                        </td>
                        <td className="text-right">{r.order_count}</td>
                        <td className="text-right">${r.gross_sales.toFixed(2)}</td>
                        <td className="text-right font-semibold">${r.tax_collected.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <p className="text-xs text-gray-400 text-center pt-4">
              Filed monthly via VA Form ST-1 (and ST-1A for multi-location). Filing deadline: 20th of the following month.
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
